/**
 * Acquisition attribution for the TWG microsite — captures where a visitor
 * came from and the anonymous identifier needed to track conversion
 * (visit → CTA click → login) in GASv3 marketing dashboards.
 *
 * Modelled on WAC's `analytics/atl-analytics/helpers.ts` (`getAndSetUtms`)
 * so the same browser produces the same `anonId` and the same UTM bag
 * regardless of which Atlassian property captured them first. We keep
 * cross-property identity stitching by:
 *
 *  - Reading (never minting) the existing Segment-style `ajs_anonymous_id`
 *    cookie. WAC, the Analytics Wrapper, and `@atlassiansox/analytics-web-client`
 *    all converge on this cookie name. If it's missing we return `null` and
 *    let the GASv3 client mint one when it sends its first event.
 *  - Persisting the captured UTM bag to `localStorage["marketingContext"]`
 *    using the same JSON envelope WAC writes: `{ domain, utm: { source,
 *    medium, campaign, content, sfdcCampaignId } }`. Cross-property
 *    backfill (`teamworkgraph.com` ← `atlassian.com`) will eventually
 *    happen via GUPS once the Analytics Wrapper is adopted; until then
 *    each property persists locally only.
 *
 * Replacement semantics (same as WAC, NOT first-touch):
 *  - URL has UTMs → overwrite localStorage with the URL UTMs.
 *  - URL has no UTMs → return whatever is in localStorage.
 *  - Neither → return null UTMs.
 *
 * The "where did the user come from" question is answered by combining
 * three things on every event:
 *  1. UTM bag (highest fidelity — direct marketing attribution).
 *  2. Paid-ad click IDs (`gclid`, `fbclid`, `msclkid`, `li_fat_id`) when
 *     UTMs are missing.
 *  3. `referrerHost` (lowest fidelity, often blank for native apps —
 *     Discord, Slack, iOS — but useful for organic search and inbound
 *     blog/news links).
 *
 * Everything is best-effort; storage failures (Brave / Safari ITP /
 * private mode) collapse to in-memory only and never throw.
 */
import { captureLandingHash, extractHashQuery } from '../deep-link';

const STORAGE_KEY_MARKETING_CONTEXT = 'marketingContext';
const COOKIE_NAME_ANON_ID = 'ajs_anonymous_id';

export type TrafficSource = 'paid' | 'organic' | 'internal' | 'referral' | 'direct';

/**
 * The attribute bag merged into every event in
 * `SCREEN_EVENTS_WITH_ATTRIBUTION` / `NON_SCREEN_EVENTS_WITH_ATTRIBUTION`
 * (see [`AnalyticsProvider.tsx`](../AnalyticsProvider.tsx)).
 *
 * Field naming mirrors the analytics.spec.yaml `AttributionContext`
 * shared anchor and (where applicable) the Atlassian Marketing Analytics
 * Pipeline runbook's wrapper payload, so events are forward-compatible
 * with the eventual Analytics Wrapper adoption.
 */
export type AttributionPayload = {
	anonId: string | null;

	trafficSource: TrafficSource;
	trafficSourceDetail: string | null;

	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmContent: string | null;
	utmTerm: string | null;
	utmSfdcCampaignId: string | null;

	gclid: string | null;
	fbclid: string | null;
	msclkid: string | null;
	liFatId: string | null;

	referrerHost: string | null;
	landingPath: string;
};

/**
 * The persisted UTM-only sub-payload, kept on disk in localStorage. Mirrors
 * the WAC `marketingContext.utm` shape so that, if/when this property
 * adopts the shared analytics wrapper, the same key is interoperable.
 */
type StoredMarketingContext = {
	domain: string;
	utm: {
		source: string;
		medium: string;
		campaign: string;
		content: string;
		sfdcCampaignId: string;
	};
};

const SEARCH_ENGINE_HOSTS: ReadonlyArray<{ match: string; family: string }> = [
	{ match: 'google.', family: 'google' },
	{ match: 'bing.com', family: 'bing' },
	{ match: 'duckduckgo.com', family: 'duckduckgo' },
	{ match: 'search.yahoo.', family: 'yahoo' },
	{ match: 'baidu.com', family: 'baidu' },
	{ match: 'yandex.', family: 'yandex' },
	{ match: 'ecosia.org', family: 'ecosia' },
	{ match: 'search.brave.com', family: 'brave' },
];

const INTERNAL_HOST_SUFFIXES: readonly string[] = [
	'atlassian.com',
	'atlassian.net',
	'jira.com',
	'confluence.com',
	'loom.com',
	'bitbucket.org',
	'trello.com',
	'statuspage.io',
];

const detailFromClickId = (
	gclid: string | null,
	fbclid: string | null,
	msclkid: string | null,
	liFatId: string | null,
): string | null => {
	if (gclid) return 'google-ads';
	if (fbclid) return 'meta-ads';
	if (msclkid) return 'bing-ads';
	if (liFatId) return 'linkedin-ads';
	return null;
};

const safeHostname = (rawUrl: string): string | null => {
	if (!rawUrl) return null;
	try {
		return new URL(rawUrl).hostname.toLowerCase();
	} catch {
		return null;
	}
};

const matchesSearchEngine = (host: string): string | null => {
	for (const engine of SEARCH_ENGINE_HOSTS) {
		if (host.includes(engine.match)) return engine.family;
	}
	return null;
};

const matchesInternalHost = (host: string): boolean =>
	INTERNAL_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));

const safeReadLocalStorage = (key: string): string | null => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

const safeWriteLocalStorage = (key: string, value: string): void => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.setItem(key, value);
	} catch {
		// Blocked storage APIs (Brave / Safari ITP / private mode) must
		// not crash the page. We silently fall back to in-memory only —
		// the event still fires, just without persisted UTMs across
		// SPA navigations.
	}
};

/**
 * Reads the Segment-style `ajs_anonymous_id` cookie. Matches WAC's
 * convention: `decodeURIComponent` then strip surrounding single/double
 * quotes (Segment writes the value as a JSON string). Returns null when
 * the cookie is missing — we deliberately do NOT mint our own anon ID,
 * because doing so would fragment identity stitching across Atlassian
 * properties.
 */
export const readAnonId = (): string | null => {
	if (typeof document === 'undefined') return null;
	try {
		const all = document.cookie ? document.cookie.split('; ') : [];
		for (const entry of all) {
			const eqIdx = entry.indexOf('=');
			if (eqIdx === -1) continue;
			if (entry.slice(0, eqIdx) === COOKIE_NAME_ANON_ID) {
				const raw = entry.slice(eqIdx + 1);
				return decodeURIComponent(raw).replace(/['"]+/g, '') || null;
			}
		}
	} catch {
		// fall through
	}
	return null;
};

const readStoredContext = (): StoredMarketingContext | null => {
	const raw = safeReadLocalStorage(STORAGE_KEY_MARKETING_CONTEXT);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<StoredMarketingContext>;
		if (!parsed || !parsed.utm) return null;
		return parsed as StoredMarketingContext;
	} catch {
		return null;
	}
};

const writeStoredContext = (context: StoredMarketingContext): void => {
	safeWriteLocalStorage(STORAGE_KEY_MARKETING_CONTEXT, JSON.stringify(context));
};

/**
 * Pure resolver — builds an `AttributionPayload` from raw inputs. No
 * side effects, fully unit-testable.
 *
 * Resolution precedence:
 *   1. UTMs present                        → `paid` if utm_medium=cpc, else `referral`
 *   2. paid click ID present               → `paid`
 *   3. referrer hostname = search engine   → `organic`
 *   4. referrer hostname = internal Atlassian → `internal`
 *   5. referrer hostname = anything else   → `referral`
 *   6. otherwise                           → `direct`
 */
export const resolveAttribution = (
	search: string,
	referrer: string,
	pathname: string,
	anonId: string | null,
): AttributionPayload => {
	const params = new URLSearchParams(search);

	const utmSource = params.get('utm_source');
	const utmMedium = params.get('utm_medium');
	const utmCampaign = params.get('utm_campaign');
	const utmContent = params.get('utm_content');
	const utmTerm = params.get('utm_term');
	const utmSfdcCampaignId = params.get('utm_sfdc-campaign_id');

	const gclid = params.get('gclid');
	const fbclid = params.get('fbclid');
	const msclkid = params.get('msclkid');
	const liFatId = params.get('li_fat_id');

	const referrerHost = safeHostname(referrer);

	let trafficSource: TrafficSource = 'direct';
	let trafficSourceDetail: string | null = null;

	if (utmSource) {
		trafficSource = utmMedium === 'cpc' ? 'paid' : 'referral';
		trafficSourceDetail = utmSource;
	} else if (gclid || fbclid || msclkid || liFatId) {
		trafficSource = 'paid';
		trafficSourceDetail = detailFromClickId(gclid, fbclid, msclkid, liFatId);
	} else if (referrerHost) {
		const engine = matchesSearchEngine(referrerHost);
		if (engine) {
			trafficSource = 'organic';
			trafficSourceDetail = engine;
		} else if (matchesInternalHost(referrerHost)) {
			trafficSource = 'internal';
			trafficSourceDetail = referrerHost;
		} else {
			trafficSource = 'referral';
			trafficSourceDetail = referrerHost;
		}
	}

	return {
		anonId,
		trafficSource,
		trafficSourceDetail,
		utmSource,
		utmMedium,
		utmCampaign,
		utmContent,
		utmTerm,
		utmSfdcCampaignId,
		gclid,
		fbclid,
		msclkid,
		liFatId,
		referrerHost,
		landingPath: pathname,
	};
};

const stripLeadingQuestion = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

/**
 * Combines two query strings; params in `primary` win over `secondary`
 * (`URLSearchParams.get` returns the first occurrence).
 */
const mergeSearch = (primary: string, secondary: string): string => {
	const parts = [stripLeadingQuestion(primary), stripLeadingQuestion(secondary)].filter(Boolean);
	return parts.length > 0 ? `?${parts.join('&')}` : '';
};

/**
 * Returns the live attribution payload for the current page-load.
 *
 * Behaviour mirrors WAC `Helpers.getAndSetUtms`:
 *  - If the URL has UTMs, persist them (overwrite any existing context)
 *    and return them.
 *  - If the URL has no UTMs but localStorage does, return the persisted
 *    UTMs alongside the live referrer + click-id signals.
 *  - If neither, return whatever the resolver derived from referrer +
 *    click IDs (or `direct` with null UTMs).
 *
 * Safe to call from SSR — returns an in-memory `direct` payload when
 * `window` is unavailable.
 */
export const getAttribution = (): AttributionPayload => {
	if (typeof window === 'undefined') {
		return resolveAttribution('', '', '/', null);
	}

	const anonId = readAnonId();
	// Allows for UTMs after the hash (`#CLI?utm_source=...`),
	// so `window.location.search` is empty. The hash fragment's query-part is
	// parsed as a fallback; a real `?query` still wins.
	const hashQuery = extractHashQuery(captureLandingHash());
	const search = mergeSearch(window.location.search, hashQuery);
	const pathname = window.location.pathname;
	const referrer = typeof document !== 'undefined' ? document.referrer : '';
	const live = resolveAttribution(search, referrer, pathname, anonId);

	if (live.utmSource || live.utmMedium || live.utmCampaign || live.utmContent || live.utmTerm) {
		// URL has UTMs — overwrite stored context (replacement semantics).
		writeStoredContext({
			domain: window.location.hostname,
			utm: {
				source: live.utmSource ?? '',
				medium: live.utmMedium ?? '',
				campaign: live.utmCampaign ?? '',
				content: live.utmContent ?? '',
				sfdcCampaignId: live.utmSfdcCampaignId ?? '',
			},
		});
		return live;
	}

	// No UTMs in URL — fall back to persisted UTMs (if any) and re-derive
	// trafficSource using the persisted UTMs + live referrer.
	const stored = readStoredContext();
	if (!stored) {
		return live;
	}

	const restoredUtmSource = stored.utm.source || null;
	const restoredUtmMedium = stored.utm.medium || null;
	let trafficSource: TrafficSource = live.trafficSource;
	let trafficSourceDetail: string | null = live.trafficSourceDetail;
	if (restoredUtmSource) {
		trafficSource = restoredUtmMedium === 'cpc' ? 'paid' : 'referral';
		trafficSourceDetail = restoredUtmSource;
	}

	return {
		...live,
		trafficSource,
		trafficSourceDetail,
		utmSource: restoredUtmSource,
		utmMedium: restoredUtmMedium,
		utmCampaign: stored.utm.campaign || null,
		utmContent: stored.utm.content || null,
		utmTerm: live.utmTerm,
		utmSfdcCampaignId: stored.utm.sfdcCampaignId || null,
	};
};

/** Test-only — clears persisted marketing context. Do not call from app code. */
export const __resetAttributionForTests = (): void => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.removeItem(STORAGE_KEY_MARKETING_CONTEXT);
	} catch {
		// noop
	}
};
